import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import { authFetch } from '../../Utilities/Api';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import './ChangePassword.css';

const ChangePassword = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [loading, setLoading] = useState(false);

    // theme
    useEffect(() => {
        const isDark = (localStorage.getItem('theme') || 'light') === 'dark';
        document.documentElement.classList.toggle('dark', isDark);
    }, []);

    // if not logged in, send to login
    useEffect(() => {
        if (!user) navigate('/login');
    }, [user, navigate]);

    // 🔐 same rules as backend validatePassword + register page
    const passwordRules = {
        length: newPassword.length >= 8,
        lower: /[a-z]/.test(newPassword),
        upper: /[A-Z]/.test(newPassword),
        number: /[0-9]/.test(newPassword),
        special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    };

    const allRulesValid = Object.values(passwordRules).every(Boolean);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        if (newPassword !== confirmNewPassword) {
            toast.error('New passwords do not match.', { icon: '💬 talkear' });
            return;
        }

        // optional but nice: stop early if rules not met (backend still enforces too)
        if (!allRulesValid) {
            toast.error('Please meet all password requirements before continuing.', {
                icon: '💬 talkear',
            });
            return;
        }

        setLoading(true);
        try {
            const res = await authFetch('/auth/change-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    currentPassword,
                    newPassword,
                }),
            });

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                toast.error(data?.message || 'Failed to change password.', { icon: '💬 talkear' });
                setLoading(false);
                return;
            }

            toast.success('✅ Password updated successfully.', { icon: '💬 talkear' });

            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            // optional: navigate('/profile');
        } catch (err) {
            console.error('❌ Change password error:', err);
            toast.error('Something went wrong. Please try again.', { icon: '💬 talkear' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-wrapper change-password-page">
            <div className="form-card change-password-card">
                <h2 className="form-title">
                    Change your password
                    <span className="security-tag">Security</span>
                </h2>

                <form onSubmit={handleSubmit} className="form-body">
                    <label>Current password</label>
                    <div className="password-wrapper">
                        <input
                            type={showCurrent ? 'text' : 'password'}
                            placeholder="Current password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            required
                            disabled={loading}
                            autoComplete="current-password"
                        />
                        <span
                            className="eye-icon"
                            onClick={() => setShowCurrent((s) => !s)}
                            title={showCurrent ? 'Hide password' : 'Show password'}
                        >
                            {showCurrent ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>

                    <label>New password</label>
                    <div className="password-wrapper">
                        <input
                            type={showNew ? 'text' : 'password'}
                            placeholder="New password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            disabled={loading}
                            autoComplete="new-password"
                        />
                        <span
                            className="eye-icon"
                            onClick={() => setShowNew((s) => !s)}
                            title={showNew ? 'Hide password' : 'Show password'}
                        >
                            {showNew ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>

                    {/* ✅ Same visual requirements list as register */}
                    <div className="password-rules">
                        <p className="password-rules-title">Your new password must contain:</p>
                        <ul>
                            <li className={passwordRules.length ? 'valid' : 'invalid'}>
                                At least 8 characters
                            </li>
                            <li className={passwordRules.lower ? 'valid' : 'invalid'}>
                                At least one lowercase letter
                            </li>
                            <li className={passwordRules.upper ? 'valid' : 'invalid'}>
                                At least one uppercase letter
                            </li>
                            <li className={passwordRules.number ? 'valid' : 'invalid'}>
                                At least one number
                            </li>
                            <li className={passwordRules.special ? 'valid' : 'invalid'}>
                                At least one special character
                            </li>
                        </ul>
                    </div>

                    <label>Confirm new password</label>
                    <input
                        type="password"
                        placeholder="Confirm new password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        disabled={loading}
                        autoComplete="new-password"
                    />

                    <button type="submit" className="form-btn" disabled={loading}>
                        {loading ? 'Updating...' : 'Update Password'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ChangePassword;
